<template>
  <BModal
    :model-value="visible"
    title="Create New User"
    :ok-disabled="!canSubmit || creating"
    ok-title="Create"
    @update:model-value="$emit('update:visible', $event)"
    @ok="onOkClick"
  >
    <!-- Error Alert -->
    <ErrorPanel
      :error-message="errorMessage"
      :error-title="errorTitle"
      :error-details="errorDetails"
      @clear="clearError"
    />

    <!-- Validation Criteria -->
    <div class="validation-guidelines mb-4 p-3 bg-light rounded">
      <div class="font-weight-bold mb-2">
        Required Information:
      </div>
      <ul class="mb-0 small">
        <li>
          <span :class="getRequirementClass('username')">
            Username must be at least 3 characters long
          </span>
        </li>
        <li>
          <span :class="getRequirementClass('email')">
            Email must be valid (e.g., user@example.com)
          </span>
        </li>
      </ul>
    </div>

    <BForm>
      <BFormGroup
        label="Username"
        label-for="username-input"   
        class="py-2"     
      >
        <BFormInput
          id="username-input"
          :model-value="form.username"
          placeholder="Enter username"
          required
          :state="usernameState"
          @update:model-value="emit('update:form', 'username', $event as string)"
        />
        <small
          v-if="form.username && form.username.length < 3"
          class="d-block text-danger mt-1"
        >
          Username must be at least 3 characters
        </small>
      </BFormGroup>
      <BFormGroup
        label="Email"
        label-for="email-input"
        class="py-2"
      >
        <BFormInput
          id="email-input"
          :model-value="form.email"
          type="email"
          placeholder="Enter email"
          required
          :state="emailState"
          @update:model-value="emit('update:form', 'email', $event as string)"
        />
        <small
          v-if="form.email && !isValidEmail(form.email)"
          class="d-block text-danger mt-1"
        >
          Please enter a valid email address
        </small>
      </BFormGroup>
      <BFormGroup
        label="Display Name"
        label-for="display-name-input"
        class="py-2"
      >
        <BFormInput
          id="display-name-input"
          :model-value="form.display_name"
          placeholder="Enter display name (optional)"
          @update:model-value="emit('update:form', 'display_name', $event as string)"
        />
      </BFormGroup>
    </BForm>
  </BModal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { BModal, BForm, BFormGroup, BFormInput, BvTriggerableEvent } from "bootstrap-vue-next";
import { useUserApi } from "@/composables/useUserApi";
import { generateRandomPassword } from "@/utils/password";
import { handleUserManagementError } from "@/utils/errorHandler";
import { useErrorPanel } from "@/composables/useErrorPanel";
import ErrorPanel from "@/components/ErrorPanel.vue";

interface CreateForm {
  username: string;
  email: string;
  display_name: string;
  password: string;
}

const userApi = useUserApi();

const props = defineProps<{
  visible: boolean;
  form: CreateForm;
  creating: boolean;
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  submit: [data: CreateForm];
  "update:form": [field: string, value: string];
}>();

const { errorMessage, errorTitle, errorDetails, clearError, setError } = useErrorPanel();
const isCreating = ref(false);

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isUsernameValid = computed(() => {
  return props.form.username && props.form.username.length >= 3;
});

const isEmailValid = computed(() => {
  return props.form.email && isValidEmail(props.form.email);
});

const canSubmit = computed(() => {
  return isUsernameValid.value && isEmailValid.value && !isCreating.value;
});

const usernameState = computed(() => {
  if (!props.form.username) return null;
  return isUsernameValid.value ? true : false;
});

const emailState = computed(() => {
  if (!props.form.email) return null;
  return isEmailValid.value ? true : false;
});

const getRequirementClass = (requirement: string): string => {
  switch (requirement) {
    case "username":
      return isUsernameValid.value ? "text-success" : "text-muted";
    case "email":
      return isEmailValid.value ? "text-success" : "text-muted";
    default:
      return "text-muted";
  }
};

const onOkClick = async (bvModalEvt: BvTriggerableEvent) => {
  // Prevent the modal from closing automatically
  bvModalEvt.preventDefault();
  
  await submitCreate();
};

const submitCreate = async () => {
  // Clear any previous errors
  clearError();

  // Validate before submission
  if (!isUsernameValid.value) {
    setError("Invalid Username", "Username must be at least 3 characters long");
    return;
  }

  if (!isEmailValid.value) {
    setError("Invalid Email", "Please enter a valid email address");
    return;
  }

  const generatedPassword = generateRandomPassword();
  isCreating.value = true;

  try {
    await userApi.createUser({
      ...props.form,
      password: generatedPassword,
    });

    // Success - emit data back to parent and close modal
    emit("submit", {
      ...props.form,
      password: generatedPassword,
    });
    
    // Close the modal after successful creation
    emit("update:visible", false);
    
    // Reset form for next use
    clearError();
  } catch (error) {
    console.error("Failed to create user:", error);
    const message = handleUserManagementError(error, "create");
    setError("Failed to Create User", message);
  } finally {
    isCreating.value = false;
  }
};
</script>
